import React, { useEffect, useRef } from "react";
import "./styles.css";

export default function App() {
  return (
    <main className="root">
      <CanvasDemo />
    </main>
  );
}

function CanvasDemo() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const scrubRef = useRef(null);

  useEffect(() => {
    const wrap = /** @type {HTMLDivElement} */ (wrapRef.current);
    const canvas = /** @type {HTMLCanvasElement} */ (canvasRef.current);
    const ctx = canvas.getContext("2d");
    const cleanupFns = [];

    const IMG_SRC = "/pond.jpg"; // in public/

    // --- config ---
    const REF_W = 2148; // PSD width
    const REF_H = 2018; // PSD height
    // fixed bottoms in REF px
    const ANCHOR_4_FROM_BOTTOM_REF = 401;
    const ANCHOR_3_FROM_BOTTOM_REF = 806;
    const ANCHOR_2_FROM_BOTTOM_REF = 1208;
    const ANCHOR_1_FROM_BOTTOM_REF = 1616;
    const FRAME_MS = 55; // uniform per-frame duration (1s like your early frames)
    const SEEK_MS = 0; // preview later frames (set back to 0 after check)
    const HOLD_MS = 0; // optional end pause
    const LOOP = false;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // device-pixel–aware snapping
    const snap = (v) => Math.round(v * dpr) / dpr;
    const snapDown = (v) => Math.floor(v * dpr) / dpr;
    const SEAM_H = 1 / dpr; // 1 physical px

    // --- offset + pause state for puzzle editing ---
    let paused = false; // Space toggles this
    let frameIndex = 0; // which keyframe is shown when paused (0-based)
    let sel = 5; // selected shutter: 1..5 (1=top, 5=bottom)

    // per-frame offsets storage: frameIndex -> {off1..off5} in REF px
    const perFrameOffsets = new Map();

    // --- baseline + animation scheduling ---
    // hard-coded baseline taken from F17 (puzzle pose)
    const BASE_OFFSETS = {
      off1: 1140, // top shutter
      off2: 820,
      off3: -410,
      off4: -1040,
      off5: 0, // bottom shutter
    };
    let BASE_SET = true; // baseline is active from the start
    let ANIM_START = Number.POSITIVE_INFINITY; // segment index (0-based) — F2 -> 0, F3 -> 1, ...
    let ANIM_DURATION_FRAMES = 12; // how many frames to glide back to aligned

    // puzzle starts returning to aligned image from frame label F18
    const RETURN_START_FRAME_LABEL = 18;
    const RETURN_START_SEG = RETURN_START_FRAME_LABEL - 2; // F2 -> 0, F3 -> 1, ...

    // per-shutter puzzle tops in REF "from-top" coordinates (we'll compute from F17)
    let P1_TOP = 0;
    let P2_TOP = 0;
    let P3_TOP = 0;
    let P4_TOP = 0;
    let P5_TOP = 0;

    function getEffectiveOffsets(i, a) {
      // If no baseline captured, fall back to per-frame edits
      if (!BASE_SET) return getOffsets(i);

      // fractional position within the timeline (i = segment index, a in [0..1])
      const pos = i + a;

      // Before animation begins: hold baseline
      if (pos < ANIM_START) return { ...BASE_OFFSETS };

      // After animation ends: perfectly aligned (zeros)
      if (i >= ANIM_START + ANIM_DURATION_FRAMES) {
        return { off1: 0, off2: 0, off3: 0, off4: 0, off5: 0 };
      }

      // During animation: baseline -> zero
      const t = Math.max(
        0,
        Math.min(1, (pos - ANIM_START) / ANIM_DURATION_FRAMES)
      );
      const lerpToZero = (v) => v + (0 - v) * t;

      return {
        off1: lerpToZero(BASE_OFFSETS.off1 || 0),
        off2: lerpToZero(BASE_OFFSETS.off2 || 0),
        off3: lerpToZero(BASE_OFFSETS.off3 || 0),
        off4: lerpToZero(BASE_OFFSETS.off4 || 0),
        off5: lerpToZero(BASE_OFFSETS.off5 || 0),
      };
    }

    function getOffsets(i) {
      let o = perFrameOffsets.get(i);
      if (!o) {
        o = { off1: 0, off2: 0, off3: 0, off4: 0, off5: 0 };
        perFrameOffsets.set(i, o);
      }
      return o;
    }

    // helper: copy the current frame's offsets to clipboard/console
    function copyFrameOffsetsLine() {
      const f = frameIndex;
      const o = getOffsets(f);
      const line = `F${f + 2}: off5=${o.off5}, off4=${o.off4}, off3=${
        o.off3
      }, off2=${o.off2}, off1=${o.off1}`;
      try {
        navigator.clipboard.writeText(line);
      } catch (_) {}
      console.log(line);
    }

    // --- image preload + mapping (cover + centered crop) ---
    const img = new Image();
    let imgReady = false;
    let IMG_SC = 1,
      IMG_OFF_X = 0,
      IMG_OFF_Y = 0;

    img.onload = () => {
      imgReady = true;
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      const sc = Math.max(REF_W / iw, REF_H / ih);
      IMG_SC = sc;
      IMG_OFF_X = (iw * sc - REF_W) / 2;
      IMG_OFF_Y = (ih * sc - REF_H) / 2;
      // restart timing so the first frame draws the image, not black
      start = 0;
    };
    img.src = IMG_SRC;

    // --- puzzle-aware helpers (support vertical offsets in REF px) ---

    // Compute source rectangle inside the original image for a REF strip,
    // applying an additional vertical offset (offRef) in REF pixels.
    function srcRectForRefStrip(
      refTopFromBottom,
      refBottomFromBottom,
      offRef = 0
    ) {
      // convert REF from-bottom → from-top, then add offset
      const topFromTop = REF_H - refTopFromBottom + offRef;
      const bottomFromTop = REF_H - refBottomFromBottom + offRef;

      const hRef = Math.max(0, bottomFromTop - topFromTop);

      // convert REF → original image pixels (undo the cover-scale + centered crop)
      const sx = (0 + IMG_OFF_X) / IMG_SC; // full width, centered
      const sy = (topFromTop + IMG_OFF_Y) / IMG_SC;
      const sw = REF_W / IMG_SC;
      const sh = hRef / IMG_SC;

      return { sx, sy, sw, sh };
    }

    // Draw one shutter strip; if image not ready, draw black so motion is still visible
    function drawStrip(refTop, refBottom, yTop, yBottom, offRef = 0) {
      const dh = snapDown(yBottom - yTop);
      if (dh <= 0) return;

      if (imgReady) {
        const { sx, sy, sw, sh } = srcRectForRefStrip(
          refTop,
          refBottom,
          offRef
        );
        ctx.drawImage(img, sx, sy, sw, sh, 0, yTop, boxW, dh);
      } else {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, yTop, boxW, dh);
      }
    }

    // 1-px shadow seam on the white side
    function drawSeam(y) {
      ctx.save();
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = "#000";
      ctx.fillRect(0, y - SEAM_H, boxW, SEAM_H);
      ctx.restore();
    }

    // --- keyframes (F2..F36). Times inside will be overwritten by uniform cadence ---
    const timeline = [
      {
        t: 0,
        bottom: 41,
        gap: REF_H - 41,
        mid: 0,
        gapTop: 0,
        mid2: 0,
        gapTop2: 0,
        top: 0,
      }, // F2
      {
        t: 1000,
        bottom: 80,
        gap: REF_H - 80,
        mid: 0,
        gapTop: 0,
        mid2: 0,
        gapTop2: 0,
        top: 0,
      }, // F3
      {
        t: 2000,
        bottom: 113,
        gap: REF_H - 113,
        mid: 0,
        gapTop: 0,
        mid2: 0,
        gapTop2: 0,
        top: 0,
      }, // F4
      {
        t: 3000,
        bottom: 173,
        gap: 1034,
        mid: 0,
        gapTop: 0,
        mid2: 0,
        gapTop2: 0,
        top: 88,
      }, // F5
      {
        t: 4000,
        bottom: 199,
        gap: 202,
        mid: 4,
        gapTop: 803,
        mid2: 0,
        gapTop2: 0,
        top: 121,
      }, // F6
      {
        t: 5000,
        bottom: 219,
        gap: 182,
        mid: 46,
        gapTop: 761,
        mid2: 0,
        gapTop2: 0,
        top: 150,
      }, // F7
      {
        t: 6000,
        bottom: 257,
        gap: 144,
        mid: 120,
        gapTop: 285,
        mid2: 4,
        gapTop2: 399,
        top: 204,
      }, // F8
      {
        t: 7000,
        bottom: 271,
        gap: 130,
        mid: 149,
        gapTop: 257,
        mid2: 47,
        gapTop2: 359,
        top: 225,
      }, // F9
      {
        t: 8000,
        bottom: 298,
        gap: 102,
        mid: 203,
        gapTop: 204,
        mid2: 119,
        gapTop2: 287,
        top: 260,
      }, // F10
      {
        t: 9000,
        bottom: 319,
        gap: 81,
        mid: 243,
        gapTop: 162,
        mid2: 177,
        gapTop2: 227,
        top: 291,
        gapTop3: 116,
        top2: 85,
      }, // F11
      {
        t: 10000,
        bottom: 337,
        gap: 63,
        mid: 276,
        gapTop: 130,
        mid2: 224,
        gapTop2: 182,
        top: 294,
        gapTop3: 91,
        top2: 150,
      }, // F12
      {
        t: 11000,
        bottom: 343,
        gap: 58,
        mid: 289,
        gapTop: 115,
        mid2: 243,
        gapTop2: 162,
        top: 301,
        gapTop3: 81,
        top2: 176,
      }, // F13
      {
        t: 12000,
        bottom: 352,
        gap: 50,
        mid: 302,
        gapTop: 103,
        mid2: 263,
        gapTop2: 144,
        top: 312,
        gapTop3: 72,
        top2: 203,
      }, // F14
      {
        t: 13000,
        bottom: 361,
        gap: 41,
        mid: 326,
        gapTop: 81,
        mid2: 291,
        gapTop2: 117,
        top: 326,
        gapTop3: 59,
        top2: 242,
      }, // F15
      {
        t: 14000,
        bottom: 370,
        gap: 32,
        mid: 342,
        gapTop: 63,
        mid2: 316,
        gapTop2: 92,
        top: 341,
        gapTop3: 45,
        top2: 277,
      }, // F16
      {
        t: 15000,
        bottom: 376,
        gap: 25,
        mid: 355,
        gapTop: 51,
        mid2: 334,
        gapTop2: 75,
        top: 355,
        gapTop3: 36,
        top2: 305,
      }, // F17
      {
        t: 18000,
        bottom: 379,
        gap: 21,
        mid: 362,
        gapTop: 45,
        mid2: 342,
        gapTop2: 64,
        top: 369,
        gapTop3: 30,
        top2: 316,
      }, // F18
      {
        t: 19000,
        bottom: 382,
        gap: 19,
        mid: 366,
        gapTop: 40,
        mid2: 348,
        gapTop2: 57,
        top: 380,
        gapTop3: 26,
        top2: 324,
      }, // F19
      {
        t: 20000,
        bottom: 387,
        gap: 13,
        mid: 379,
        gapTop: 27,
        mid2: 367,
        gapTop2: 40,
        top: 387,
        gapTop3: 20,
        top2: 347,
      }, // F20
      {
        t: 21000,
        bottom: 390,
        gap: 12,
        mid: 380,
        gapTop: 25,
        mid2: 372,
        gapTop2: 33,
        top: 390,
        gapTop3: 16,
        top2: 355,
      }, // F21
      {
        t: 22000,
        bottom: 392,
        gap: 9,
        mid: 385,
        gapTop: 23,
        mid2: 375,
        gapTop2: 29,
        top: 392,
        gapTop3: 13,
        top2: 362,
      }, // F22
      {
        t: 23000,
        bottom: 394,
        gap: 7,
        mid: 389,
        gapTop: 17,
        mid2: 381,
        gapTop2: 25,
        top: 394,
        gapTop3: 11,
        top2: 372,
      }, // F23
      {
        t: 24000,
        bottom: 395,
        gap: 6,
        mid: 393,
        gapTop: 13,
        mid2: 388,
        gapTop2: 19,
        top: 396,
        gapTop3: 9,
        top2: 378,
      }, // F24
      {
        t: 25000,
        bottom: 395,
        gap: 5,
        mid: 394,
        gapTop: 9,
        mid2: 388,
        gapTop2: 16,
        top: 399,
        gapTop3: 7,
        top2: 381,
      }, // F25
      {
        t: 26000,
        bottom: 397,
        gap: 2,
        mid: 397,
        gapTop: 7,
        mid2: 392,
        gapTop2: 11,
        top: 400,
        gapTop3: 3,
        top2: 386,
      }, // F26
      {
        t: 27000,
        bottom: 397,
        gap: 1,
        mid: 398,
        gapTop: 6,
        mid2: 395,
        gapTop2: 8,
        top: 400,
        gapTop3: 3,
        top2: 390,
      }, // F27
      // F29
      {
        bottom: 399,
        gap: 0,
        mid: 401,
        gapTop: 2,
        mid2: 399,
        gapTop2: 4,
        top: 400,
        gapTop3: 3,
        top2: 395,
      },
      // F30
      {
        bottom: 399,
        gap: 0,
        mid: 401,
        gapTop: 2,
        mid2: 399,
        gapTop2: 4,
        top: 400,
        gapTop3: 3,
        top2: 395,
      },
      // F31
      {
        bottom: 399,
        gap: 0,
        mid: 401,
        gapTop: 2,
        mid2: 399,
        gapTop2: 4,
        top: 403,
        gapTop3: 0,
        top2: 396,
      },
      // F32
      {
        bottom: 399,
        gap: 0,
        mid: 401,
        gapTop: 2,
        mid2: 399,
        gapTop2: 0,
        top: 403,
        gapTop3: 0,
        top2: 396,
      },
      // F33
      {
        bottom: 399,
        gap: 0,
        mid: 401,
        gapTop: 2,
        mid2: 400,
        gapTop2: 3,
        top: 403,
        gapTop3: 0,
        top2: 399,
      },
      // F34
      {
        bottom: 399,
        gap: 0,
        mid: 401,
        gapTop: 2,
        mid2: 400,
        gapTop2: 3,
        top: 403,
        gapTop3: 0,
        top2: 399,
      },
      // F35
      {
        bottom: 399,
        gap: 0,
        mid: 403,
        gapTop: 0,
        mid2: 401,
        gapTop2: 2,
        top: 403,
        gapTop3: 0,
        top2: 400,
      },
      // F36
      {
        bottom: "same",
        gap: "0",
        mid: "same",
        gapTop: "0",
        mid2: 401,
        gapTop2: 2,
        top: "same",
        gapTop3: "0",
        top2: "same",
      },
      // F37
      // F38
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: 403,
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: 401,
      },
      // F39
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F40
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: 403,
      },
      // F41
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F42
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F43
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F44
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F45
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F46
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F47
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F48
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F49
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F50
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F51
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F52
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F53
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F54
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F55
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F56
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F57
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F58
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
      // F59
      {
        bottom: "same",
        gap: 0,
        mid: "same",
        gapTop: 0,
        mid2: "same",
        gapTop2: 0,
        top: "same",
        gapTop3: 0,
        top2: "same",
      },
    ];

    // Normalize: turn "same"/"0" → numbers, fill missing from previous
    (function normalizeTimeline() {
      const KEYS = [
        "bottom",
        "gap",
        "mid",
        "gapTop",
        "mid2",
        "gapTop2",
        "top",
        "gapTop3",
        "top2",
        "off5",
        "off4",
        "off3",
        "off2",
        "off1", // NEW: per-shutter image offsets (REF px)
      ];
      for (let i = 0; i < timeline.length; i++) {
        const f = timeline[i];
        const prev = i > 0 ? timeline[i - 1] : null;
        for (const k of KEYS) {
          let v = f[k];
          if (typeof v === "string") {
            const s = v.trim().toLowerCase();
            if (s === "0") v = 0;
            else if (s === "same") v = prev ? prev?.[k] ?? 0 : 0;
            else if (!Number.isNaN(Number(s))) v = Number(s);
          }
          if (v === undefined || v === null || v === "") {
            v = prev ? prev?.[k] ?? 0 : 0;
          }
          f[k] = v;
        }
      }
    })();

    // --- bake a softly smoothed version of the key data (used only for motion) ---
    function smoothTimelineKey(key, passes = 1) {
      for (let p = 0; p < passes; p++) {
        const tmp = new Array(timeline.length);
        for (let i = 0; i < timeline.length; i++) {
          const prev = i > 0 ? timeline[i - 1][key] : timeline[i][key];
          const curr = timeline[i][key];
          const next =
            i < timeline.length - 1 ? timeline[i + 1][key] : timeline[i][key];

          // simple moving average
          let v = (prev + curr + next) / 3;

          // keep exact zeros (closed gaps etc.) perfectly closed
          if (curr === 0) v = 0;

          tmp[i] = v;
        }
        // store as s_<key> so we don't lose the original values
        for (let i = 0; i < timeline.length; i++) {
          timeline[i][`s_${key}`] = tmp[i];
        }
      }
    }

    // bake smoothed versions of all animated keys
    const SMOOTH_PASSES = 2; // small, just enough to ease hiccups
    [
      "bottom",
      "gap",
      "mid",
      "gapTop",
      "mid2",
      "gapTop2",
      "top",
      "gapTop3",
      "top2",
    ].forEach((key) => smoothTimelineKey(key, SMOOTH_PASSES));

    // --- UNLOCK for shutter #2 (first safe segment to allow #2 to kiss #3) ---
    const UNLOCK_2_SEG = (() => {
      for (let seg = 0; seg < timeline.length - 1; seg++) {
        const k0 = timeline[seg];
        const k1 = timeline[seg + 1];

        // gap between #3 and #2 intended to be 0 at BOTH ends of this segment
        const zeroGap = k0.gapTop2 === 0 && k1.gapTop2 === 0;

        // both shutters exist (some positive height in either keyframe of the segment)
        const hasBar3 = k0.mid2 > 0 || k1.mid2 > 0; // shutter #3 height present?
        const hasBar2 = k0.top > 0 || k1.top > 0; // shutter #2 height present?

        if (zeroGap && hasBar3 && hasBar2) return seg; // first safe segment
      }
      return Number.POSITIVE_INFINITY; // never unlock if conditions never met
    })();

    // Compute the puzzle strip "tops" from F17 (label) so we can keep them frozen
    (function computePuzzleTopsFromF17() {
      const FRAME_LABEL = 17; // F17 is your reference puzzle pose
      const seg = FRAME_LABEL - 2; // F2 -> 0, F3 -> 1, ..., F17 -> 15
      const f = timeline[seg];

      const H5 = f.bottom;
      const G5 = f.gap;
      const H4 = f.mid;
      const G4 = f.gapTop;
      const H3 = f.mid2;
      const G3 = f.gapTop2;
      const H2 = f.top;
      const G2 = f.gapTop3 ?? 0;
      const H1 = f.top2 ?? 0;

      const zero54 = f.gap === 0;
      const zero43 = f.gapTop === 0;
      const zero32 = f.gapTop2 === 0;
      const zero21 = f.gapTop3 === 0;
      const canCollapse2 = zero32 && seg >= UNLOCK_2_SEG;

      let B5 = H5;
      let T5 = B5;

      const B4_req = T5 + G5;
      let B4 = zero54 ? B4_req : ANCHOR_4_FROM_BOTTOM_REF;
      let T4 = B4 + H4;

      const B3_req = T4 + G4;
      let B3 = zero43 ? B3_req : ANCHOR_3_FROM_BOTTOM_REF;
      let T3 = B3 + H3;

      const B2_req = T3 + G3;
      let B2 = canCollapse2 ? B2_req : ANCHOR_2_FROM_BOTTOM_REF;
      let T2 = B2 + H2;

      const B1_req = T2 + G2;
      let B1 = zero21 ? B1_req : ANCHOR_1_FROM_BOTTOM_REF;
      let T1 = B1 + H1;

      // Convert to "from-top" REF coordinates and apply your F17 offsets
      P5_TOP = REF_H - T5 + BASE_OFFSETS.off5;
      P4_TOP = REF_H - T4 + BASE_OFFSETS.off4;
      P3_TOP = REF_H - T3 + BASE_OFFSETS.off3;
      P2_TOP = REF_H - T2 + BASE_OFFSETS.off2;
      P1_TOP = REF_H - T1 + BASE_OFFSETS.off1;

      console.log("Puzzle tops (REF from top):", {
        P1_TOP,
        P2_TOP,
        P3_TOP,
        P4_TOP,
        P5_TOP,
      });
    })();

    // Uniform per-frame timing (overwrite any t that might exist on items)
    timeline.forEach((f, i) => {
      f.t = i * FRAME_MS;
    });
    const T_TOTAL = timeline[timeline.length - 1].t + HOLD_MS;

    // --- scrub bar wiring (drag to jump to any frame) ---
    const scrub = /** @type {HTMLInputElement} */ (scrubRef.current);
    if (scrub) {
      // frames are 0..(last segment)
      scrub.min = "0";
      scrub.max = String(timeline.length - 2);
      scrub.step = "1";
      scrub.value = "0";

      const onScrubInput = () => {
        // when user drags, show that frame exactly
        paused = true;
        const v = parseInt(scrub.value, 10) || 0;
        frameIndex = Math.max(0, Math.min(timeline.length - 2, v));
      };

      scrub.addEventListener("input", onScrubInput);
      cleanupFns.push(() => scrub.removeEventListener("input", onScrubInput));
    }

    // --- state ---
    let boxW = 0,
      boxH = 0;
    let start = 0;
    let raf = 0;
    let debug = false;

    const clamp01 = (x) => Math.max(0, Math.min(1, x));
    const lerp = (a, b, t) => a + (b - a) * t;

    // Smooth interpolation helper (Catmull–Rom spline in time)
    function smoothKey(key, i, a) {
      // i = current segment index, a in [0,1] between timeline[i] and timeline[i+1]
      const lastIdx = timeline.length - 1;

      const i0 = Math.max(0, i - 1);
      const i1 = i;
      const i2 = Math.min(lastIdx, i + 1);
      const i3 = Math.min(lastIdx, i + 2);

      const p0 = timeline[i0][key];
      const p1 = timeline[i1][key];
      const p2 = timeline[i2][key];
      const p3 = timeline[i3][key];

      const t = a;
      const t2 = t * t;
      const t3 = t2 * t;

      // Standard Catmull–Rom spline (centripetal-ish, uniform knots)
      const v =
        0.5 *
        (2 * p1 +
          (-p0 + p2) * t +
          (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
          (-p0 + 3 * p1 - 3 * p2 + p3) * t3);

      // We never want negative heights/gaps
      return Math.max(0, v);
    }

    const sizeBox = () => {
      if (!wrap || !canvas) return;

      // Let the parent (Framer / page) control the wrapper size
      wrap.style.width = "100%";
      wrap.style.height = "100%";

      const rect = wrap.getBoundingClientRect();
      let w = rect.width;
      let h = rect.height;

      // Fallback for standalone preview (when rect is 0 in some environments)
      if (!w || !h) {
        w = window.innerWidth;
        h = window.innerHeight;
      }

      // Canvas fills the wrapper
      canvas.style.width = "100%";
      canvas.style.height = "100%";

      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      boxW = w;
      boxH = h;
    };

    const draw = (tNow) => {
      // Hold animation until image is ready (no black bars)
      if (!imgReady) {
        // optional: keep canvas white so nothing ugly flashes
        ctx.clearRect(0, 0, boxW, boxH);
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, boxW, boxH);

        raf = requestAnimationFrame(draw);
        return;
      }
      if (!start) start = tNow || performance.now();
      let elapsed = (tNow || performance.now()) - start + SEEK_MS;
      if (!LOOP && elapsed > T_TOTAL) elapsed = T_TOTAL;

      // segment (paused shows an exact frame; playing interpolates)
      let i = Math.min(Math.floor(elapsed / FRAME_MS), timeline.length - 2);
      if (paused) i = Math.min(frameIndex, timeline.length - 2);

      // keep the scrub bar in sync while playing (but don't fight the user while dragging)
      const scrub = scrubRef.current;
      if (scrub && !scrub.matches(":active")) {
        const want = String(i);
        if (scrub.value !== want) scrub.value = want;
      }

      const k0 = timeline[i];
      const k1 = timeline[i + 1];
      const span = Math.max(1, k1.t - k0.t);

      // base linear segment param
      const aBase = paused
        ? 0
        : Math.max(0, Math.min(1, (elapsed - k0.t) / span));

      // keep a separate 'a' so the rest of the code (debug, offsets, puzzle) can use it
      const a = aBase;

      // background
      ctx.clearRect(0, 0, boxW, boxH);
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, boxW, boxH);

      // use baked smoothed keys for shutters 3–5 (they cause most of the jitter)
      const H5 = k0.s_bottom + (k1.s_bottom - k0.s_bottom) * a;
      const G5 = k0.s_gap + (k1.s_gap - k0.s_gap) * a;

      const H4 = k0.s_mid + (k1.s_mid - k0.s_mid) * a;
      const G4 = k0.s_gapTop + (k1.s_gapTop - k0.s_gapTop) * a;

      const H3 = k0.s_mid2 + (k1.s_mid2 - k0.s_mid2) * a;
      const G3 = k0.s_gapTop2 + (k1.s_gapTop2 - k0.s_gapTop2) * a;

      // BUT keep shutter #2 (top) and #1 fully linear to avoid fake pauses
      const H2 = k0.top + (k1.top - k0.top) * a;
      const G2 = k0.gapTop3 + (k1.gapTop3 - k0.gapTop3) * a;

      const H1 = k0.top2 + (k1.top2 - k0.top2) * a;

      // intended zero-gap flags (exact 0 at BOTH keyframes for this segment)
      const zero54 = k0.gap === 0 && k1.gap === 0;
      const zero43 = k0.gapTop === 0 && k1.gapTop === 0;
      const zero32 = k0.gapTop2 === 0 && k1.gapTop2 === 0;
      const zero21 = k0.gapTop3 === 0 && k1.gapTop3 === 0;
      const canCollapse2 = zero32 && i >= UNLOCK_2_SEG;

      // --- anchored bottoms in REF px (anchor until that gap is INTENDED to be 0) ---
      let B5 = H5;
      let T5 = B5;

      const B4_req = T5 + G5;
      let B4 = zero54 ? B4_req : ANCHOR_4_FROM_BOTTOM_REF;
      let T4 = B4 + H4;

      const B3_req = T4 + G4;
      let B3 = zero43 ? B3_req : ANCHOR_3_FROM_BOTTOM_REF;
      let T3 = B3 + H3;

      const B2_req = T3 + G3;
      let B2 = canCollapse2 ? B2_req : ANCHOR_2_FROM_BOTTOM_REF;
      let T2 = B2 + H2;

      const B1_req = T2 + G2;
      let B1 = zero21 ? B1_req : ANCHOR_1_FROM_BOTTOM_REF;
      let T1 = B1 + (H1 || 0);

      // to SCREEN px (top-origin) with crisp edges
      const s = boxH / REF_H;

      const y5Top = snap(boxH - T5 * s);
      const y5Bottom = snap(boxH - 0 * s);

      const y4Bottom = snap(boxH - B4 * s);
      const y4Top = snap(boxH - T4 * s);

      const y3Bottom = snap(boxH - B3 * s);
      const y3Top = snap(boxH - T3 * s);

      const y2Bottom = snap(boxH - B2 * s);
      const y2Top = snap(boxH - T2 * s);

      const y1Bottom = snap(boxH - B1 * s);
      const y1Top = snap(boxH - T1 * s);

      // visible white gaps (for seams only)
      const g54 = Math.max(0, y4Bottom - y5Top);
      const g43 = Math.max(0, y3Bottom - y4Top);
      const g32 = Math.max(0, y2Bottom - y3Top);
      const g21 = Math.max(0, y1Bottom - y2Top);

      // --- DEBUG OVERLAY (press R) ---
      if (debug) {
        ctx.save();
        ctx.fillStyle = "rgba(0,0,0,0.7)";
        ctx.font = "12px monospace";
        const toRef = (px) => Math.round(px / (boxH / REF_H));
        const lines = [
          `seg ${i + 2}→${i + 3}  a=${aBase.toFixed(2)}`,
          `zero: 54=${k0.gap === 0 && k1.gap === 0} 43=${
            k0.gapTop === 0 && k1.gapTop === 0
          } 32=${k0.gapTop2 === 0 && k1.gapTop2 === 0} 21=${
            k0.gapTop3 === 0 && k1.gapTop3 === 0
          }`,
          `g32(px)=${Math.round(g32)}  (ref)=${toRef(g32)}`,
          `B2(ref)=${Math.round(B2)}  anchor(ref)=${Math.round(
            ANCHOR_2_FROM_BOTTOM_REF
          )}`,
        ];
        let y = 16;
        for (const t of lines) {
          ctx.fillText(t, 10, y);
          y += 16;
        }

        // anchor guide lines
        ctx.globalAlpha = 0.25;
        const scale = boxH / REF_H;
        ctx.fillRect(0, snap(boxH - ANCHOR_2_FROM_BOTTOM_REF * scale), boxW, 1);
        ctx.fillRect(0, snap(boxH - ANCHOR_3_FROM_BOTTOM_REF * scale), boxW, 1);
        ctx.fillRect(0, snap(boxH - ANCHOR_4_FROM_BOTTOM_REF * scale), boxW, 1);
        ctx.restore();
      }

      // --- PUZZLE RETURN: keep F17 pose until F18, then glide back to aligned image ---

      // how far we are into the "return" phase (0..1)
      let u = 0;
      const segPos = i + aBase; // segment index + local interpolation
      const END_SEG = timeline.length - 2; // last animated segment

      if (segPos >= RETURN_START_SEG) {
        const total = Math.max(1, END_SEG - RETURN_START_SEG);
        u = Math.max(0, Math.min(1, (segPos - RETURN_START_SEG) / total));
      }
      // u = 0 → F17 puzzle pose
      // u = 1 → fully aligned image following the shutters

      // For each shutter j:
      //   naturalTop = REF_H - Tj        (aligned image, no offset)
      //   frozenTop  = Pj_TOP           (puzzle pose from F17)
      //   desiredTop = frozenTop + (naturalTop - frozenTop) * u
      //   offj       = desiredTop - naturalTop
      const nat5 = REF_H - T5;
      const nat4 = REF_H - T4;
      const nat3 = REF_H - T3;
      const nat2 = REF_H - T2;
      const nat1 = REF_H - T1;

      const des5 = P5_TOP + (nat5 - P5_TOP) * u;
      const des4 = P4_TOP + (nat4 - P4_TOP) * u;
      const des3 = P3_TOP + (nat3 - P3_TOP) * u;
      const des2 = P2_TOP + (nat2 - P2_TOP) * u;
      const des1 = P1_TOP + (nat1 - P1_TOP) * u;

      const off5 = des5 - nat5;
      const off4 = des4 - nat4;
      const off3 = des3 - nat3;
      const off2 = des2 - nat2;
      const off1 = des1 - nat1;

      // draw image strips (bottom → top)
      drawStrip(T5, 0, y5Top, y5Bottom, off5);
      if (!zero54 && g54 >= SEAM_H) drawSeam(y4Bottom);

      drawStrip(T4, B4, y4Top, y4Bottom, off4);
      if (!zero43 && g43 >= SEAM_H) drawSeam(y3Bottom);

      drawStrip(T3, B3, y3Top, y3Bottom, off3);
      if (!zero32 && g32 >= SEAM_H) drawSeam(y2Bottom);

      drawStrip(T2, B2, y2Top, y2Bottom, off2);
      if (!zero21 && g21 >= SEAM_H) drawSeam(y1Bottom);

      // top shutter
      drawStrip(T1, B1, y1Top, y1Bottom, off1);

      // keep animating
      if (LOOP && elapsed >= T_TOTAL) start = tNow || performance.now();
      raf = requestAnimationFrame(draw);
    }; // ← this closes the draw() function

    // Keep canvas in sync with wrapper size (for Framer / embeds)
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => {
        sizeBox();
      });
      ro.observe(wrap);
      cleanupFns.push(() => ro.disconnect());
    }

    const onKey = (e) => {
      const k = e.key;
      if (!k) return;

      // capture current frame's offsets as the global baseline pose
      if (k === "b" || k === "B") {
        const src = getOffsets(frameIndex);
        BASE_OFFSETS.off1 = src.off1 || 0;
        BASE_OFFSETS.off2 = src.off2 || 0;
        BASE_OFFSETS.off3 = src.off3 || 0;
        BASE_OFFSETS.off4 = src.off4 || 0;
        BASE_OFFSETS.off5 = src.off5 || 0;
        BASE_SET = true;
        console.log("Baseline set from F" + (frameIndex + 2), BASE_OFFSETS);
        return;
      }

      // choose when the animation back to aligned should start (by frame label F..)
      if (k === "s" || k === "S") {
        const maxF = timeline.length;
        const ask = prompt(
          `Start animating at which frame label? (2..${maxF})`
        );
        if (ask != null) {
          const n = Math.floor(Number(ask));
          if (!Number.isNaN(n)) {
            ANIM_START = Math.max(0, Math.min(n - 2, timeline.length - 2));
            console.log("Animation start set to F" + n);
          }
        }
        return;
      }

      // set glide duration (in frames)
      if (k === "d" || k === "D") {
        const ask = prompt(
          "Animation duration (frames)?",
          String(ANIM_DURATION_FRAMES)
        );
        if (ask != null) {
          const n = Math.max(1, Math.floor(Number(ask)));
          if (!Number.isNaN(n)) {
            ANIM_DURATION_FRAMES = n;
            console.log(
              "Animation duration set to",
              ANIM_DURATION_FRAMES,
              "frames"
            );
          }
        }
        return;
      }

      // clear baseline (show only per-frame offsets again)
      if (k === "x" || k === "X") {
        BASE_SET = false;
        console.log("Baseline cleared");
        return;
      }

      // --- direct goto by number (e.g. press G, type 38, jump to F38) ---
      if (k === "g" || k === "G") {
        e.preventDefault();
        const maxF = timeline.length; // last label (F..)
        const ask = prompt(`Go to frame (2..${maxF})`);
        if (ask) {
          const n = Math.floor(Number(ask));
          if (!Number.isNaN(n)) {
            // Labels are F2..F??, frameIndex is 0-based (F2 => 0)
            const idx = Math.max(0, Math.min(n - 2, timeline.length - 2));
            frameIndex = idx;
            paused = true;
          }
        }
        return;
      }

      // play/pause
      if (k === " ") {
        e.preventDefault();
        paused = !paused;
        if (paused) {
          const approx = Math.min(
            Math.floor((performance.now() - start + SEEK_MS) / FRAME_MS),
            timeline.length - 2
          );
          frameIndex = Math.max(0, Math.min(approx, timeline.length - 2));
        }
        return;
      }

      // debug overlay
      if (k === "r" || k === "R") {
        debug = !debug;
        return;
      }

      // quick jumps always force pause so you land exactly on the frame
      const forcePause = () => {
        paused = true;
      };

      // Home / End to first / last frame
      if (k === "Home") {
        frameIndex = 0;
        forcePause();
        return;
      }
      if (k === "End") {
        frameIndex = timeline.length - 2;
        forcePause();
        return;
      }

      // Step frames: [ / ] with larger steps via Shift (x5) or Alt/Option (x10)
      if (k === "[" || k === "]") {
        let step = 1;
        if (e.shiftKey) step = 5;
        if (e.altKey || e.metaKey) step = 10;
        const dir = k === "]" ? +1 : -1;
        frameIndex = Math.max(
          0,
          Math.min(timeline.length - 2, frameIndex + dir * step)
        );
        forcePause();
        return;
      }

      // shutter selection 1..5 (1=top..5=bottom)
      if ("12345".includes(k)) {
        sel = parseInt(k, 10);
        return;
      }

      // reset offsets for this frame
      if (k === "0") {
        perFrameOffsets.set(frameIndex, {
          off1: 0,
          off2: 0,
          off3: 0,
          off4: 0,
          off5: 0,
        });

        return;
      }

      // copy offsets line
      if (k === "c" || k === "C") {
        copyFrameOffsetsLine();
        return;
      }

      // nudge selected shutter's image (vertical, REF px)
      if (k === "ArrowUp" || k === "ArrowDown") {
        const o = getOffsets(frameIndex);
        const delta = (e.shiftKey ? 2 : 10) * (k === "ArrowUp" ? -1 : 1);
        o[`off${sel}`] = (o[`off${sel}`] || 0) + delta;
        perFrameOffsets.set(frameIndex, o);
        return;
      }
    };

    // init
    const onResize = () => sizeBox();
    sizeBox();
    raf = requestAnimationFrame(draw);
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);

    return () => {
      cleanupFns.forEach((fn) => {
        try {
          fn();
        } catch {}
      });
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <section className="hero" ref={wrapRef}>
      <canvas ref={canvasRef} className="hero__canvas" />
    </section>
  );
}
